import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactTable, getCoreRowModel, flexRender, type ColumnDef } from '@tanstack/react-table';
import { ChevronRight, Copy, Cable } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime } from './relative-time';
import { copyToClipboard } from '@/lib/format';
import type { LicenseRequest, LicenseRequestStatus } from '@/schemas/license-requests';

const STATUS_TEXT_CLASS: Record<LicenseRequestStatus, string> = {
  submitted: 'text-status-pending',
  approved: 'text-status-active',
  rejected: 'text-status-revoked',
};

const STATUS_DOT_CLASS: Record<LicenseRequestStatus, string> = {
  submitted: 'bg-status-pending',
  approved: 'bg-status-active',
  rejected: 'bg-status-revoked',
};

const STATUS_LABEL_KEYS: Record<LicenseRequestStatus, string> = {
  submitted: 'requests.status.submitted',
  approved: 'requests.status.approved',
  rejected: 'requests.status.rejected',
};

export function RequestStatusWord({
  status,
  rejectionReason,
}: {
  status: LicenseRequestStatus;
  rejectionReason?: string | null;
}) {
  const { t } = useTranslation();
  const word = (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-medium ${STATUS_TEXT_CLASS[status]}`}
    >
      <span className={`h-2 w-2 rounded-full ${STATUS_DOT_CLASS[status]}`} aria-hidden="true" />
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );

  if (status === 'rejected' && rejectionReason) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help underline decoration-dotted underline-offset-4">
              {word}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[320px]">
            <p className="text-xs">{rejectionReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return word;
}

interface RequestsTableProps {
  data: LicenseRequest[];
}

export function RequestsTable({ data }: RequestsTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<LicenseRequest>[]>(
    () => [
      {
        accessorKey: 'serial',
        header: t('requests.table.serial'),
        cell: ({ row }) => {
          const serial = row.original.serial;
          return (
            <div className="group flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="max-w-[220px] truncate font-mono text-[13.5px] font-medium text-foreground">
                      {serial}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="font-mono text-xs">{serial}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                type="button"
                className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                aria-label={t('common.copySerial')}
                onClick={(e) => {
                  e.stopPropagation();
                  void copyToClipboard(serial).then(() => toast.success(t('common.copied')));
                }}
              >
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'buyerCompany',
        header: t('requests.table.buyer'),
        cell: ({ row }) => (
          <span className="text-[13.5px] text-foreground">{row.original.buyerCompany}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: t('requests.table.status'),
        cell: ({ row }) => (
          <RequestStatusWord
            status={row.original.status}
            rejectionReason={row.original.rejectionReason}
          />
        ),
      },
      {
        accessorKey: 'offlineRequested',
        header: t('requests.table.offline'),
        cell: ({ row }) =>
          row.original.offlineRequested ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
                    <Cable className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('requests.offlineYes')}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="font-mono text-xs">{row.original.offlineRequestedAt ?? ''}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-[13px] text-faint">—</span>
          ),
      },
      {
        accessorKey: 'createdAt',
        header: t('requests.table.created'),
        cell: ({ row }) => (
          <span className="text-[13px] text-muted-foreground">
            <RelativeTime date={row.original.createdAt} />
          </span>
        ),
      },
      {
        id: 'chevron',
        header: '',
        cell: () => <ChevronRight className="h-4 w-4 text-faint" aria-hidden="true" />,
      },
    ],
    [t]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mt-6 overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id} className="whitespace-nowrap text-[12.5px]">
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              onClick={() => navigate(`/requests/${row.original.id}`)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
